import FallbackClassImg from "assets/fallback.png";
import {useQuery} from "@tanstack/react-query";
import {getTeacherDetailsById} from "api/teacher.api.ts";
import styled from "styled-components";
import {mediaSizes, themeColor, themeFontSize} from "ui/theme";
import {UsersIcon, WebIcon} from "ui/icons";
import {StarIcon} from "ui/icons/inputIcons/StarIcon";
import {getAvatarUrl} from "utils";
import {Link} from "react-router-dom";
import {userRoute} from "pages/user/user.routes";
import {hideScrollBar} from "ui/base/scrollbarStyle/scrollbarStyle";
import { LoaderComponent, PAGE_HEIGHT } from "ui/base/loading/LoaderComponent";
import {onlineTypeMap} from "conts";

export const ClassSection = ({classId}) => {
    if (!classId) return null;

    const {data, isLoading} = useQuery({
        queryKey: ["getTeacherDetailsById", classId],
        queryFn: () => getTeacherDetailsById({id: classId}),
        enabled: !!classId,
    });

    const classDetail = data?.data;

    if (isLoading)
        return (
            <LoaderComponent minHeight={PAGE_HEIGHT}/>
        );

    if (!classDetail) return null;

    return (
        <Container>
            <CardContainer>
                <div>
                    <Link to={`${userRoute.classDetail}${classDetail.teacher_data.user_id}`}>
                        <ImagePlaceholder>
                            {classDetail.teacher_data.profile_pic ? (
                                <img
                                    src={getAvatarUrl(classDetail.teacher_data.profile_pic)}
                                    alt="Class"
                                />
                            ) : (
                                <img src={FallbackClassImg} alt="Class"/>
                            )}
                        </ImagePlaceholder>
                    </Link>
                </div>
                <Title>
                    <Link to={`${userRoute.classDetail}${classDetail.teacher_data.user_id}`}>
                        {classDetail.teacher_data.subject_sub_category}
                    </Link>
                </Title>
                <FlexColumn>
                    <RatingSection>
                        <StarIcon color="#FF827A" size={20}/>
                        <RatingText>
                            {Number(classDetail.teacher.rate || 0).toFixed(1)}{" "}
                            ({classDetail.teacher.review || 0} reviews)
                        </RatingText>
                    </RatingSection>
                    <IconTextWrapper>
                        <UsersIcon size={20}/> <p>{classDetail.teacher.type_of_class}</p>
                    </IconTextWrapper>
                    <IconTextWrapper>
                        <WebIcon size={24}/> <p>{onlineTypeMap?.[classDetail.teacher.online_type] || ''}</p>
                    </IconTextWrapper>
                </FlexColumn>
                <Description>
                    {classDetail.teacher.experience_on_subject ? (
                        <p>{classDetail.teacher.experience_on_subject}</p>
                    ) : (
                        <div>
                            <PlaceholderText width={100}/>
                            <PlaceholderText width={70}/>
                            <PlaceholderText width={100}/>
                            <PlaceholderText width={70}/>
                            <PlaceholderText width={100}/>
                        </div>
                    )}
                </Description>
                <MoreLink>
                    <Link
                        to={`${userRoute.classDetail}${classDetail.teacher_data.user_id}`}
                    >
                        See more
                    </Link>
                </MoreLink>
            </CardContainer>
        </Container>
    );
};

const Container = styled.div`
    display: none;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
    border-left: 1px solid ${themeColor("buttonBgNeutral")};
    overflow: hidden;
    flex-grow: 1;
    @media (width >= ${mediaSizes.lg}) {
        display: flex;
    }
`;

const CardContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;
    ${hideScrollBar}
`;

const ImagePlaceholder = styled.div`
    width: 100%;
    height: 150px;
    background-color: ${themeColor("buttonBgNeutral")};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const Title = styled.h5`
    font-size: 28px;
    font-weight: 600;
    margin-top: 15px;
    margin-bottom: 15px;
`;

const RatingSection = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
`;

const RatingText = styled.span`
    font-size: 0.875rem;
    color: #FF827A;
`;

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const IconTextWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${themeColor("txt")};

    & > p {
        font-size: ${themeFontSize("small")};
        opacity: 0.6;
    }
`;

const Description = styled.div`
    margin-top: 1rem;

    p {
        font-size: 0.875rem;
        color: ${themeColor("txt")};
        line-height: 1.5;
        margin: 0;
        overflow: hidden;
        word-break: break-word;
        overflow-wrap: break-word;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 8;
    }

    div {
        display: grid;
        gap: 5px;
    }
`;

const PlaceholderText = styled.div`
    width: ${({width}) => (width ? width + "%" : "85%")};
    height: 13px;
    border-radius: 5px;
    background: ${themeColor("buttonBgNeutral")};
`;

const MoreLink = styled.a`
    font-size: 0.875rem;
    font-weight: 600;
    color: ${themeColor("primary")};
    text-decoration: underline;
    cursor: pointer;
    margin-top: 25px;

    &:hover {
        text-decoration: none;
    }
`;

